import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "@/lib/design";

const links = [
    { to: "/", label: "Home", testid: "nav-home" },
    { to: "/about", label: "About", testid: "nav-about" },
    { to: "/program", label: "Our Program", testid: "nav-program" },
    { to: "/who-we-serve", label: "Who We Serve", testid: "nav-who" },
    { to: "/referrals", label: "Referrals", testid: "nav-referrals" },
    { to: "/contact", label: "Contact", testid: "nav-contact" },
];

const Logo = () => (
    <div className="flex items-center gap-3" data-testid="brand-logo">
        <img
            src={BRAND.logo}
            alt="Crossroads to Change Residential Treatment Center"
            className="h-12 w-12 object-contain drop-shadow-sm"
            data-testid="brand-logo-img"
        />
        <span className="flex flex-col leading-none">
            <span className="font-[Outfit] text-[15.5px] font-semibold tracking-tight text-[#1A2230]">
                Crossroads <span className="text-[#0E2A47]">to Change</span>
            </span>
            <span className="text-[10px] text-[#C8A24A] font-semibold tracking-[0.18em] uppercase mt-1">
                Residential Treatment Center
            </span>
        </span>
    </div>
);

export default function Navbar() {
    const [open, setOpen] = useState(false);
    return (
        <header
            className="sticky top-0 z-40 backdrop-blur-xl bg-[#F7F5F0]/85 border-b border-[#E5DFD3]"
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 h-20 flex items-center justify-between">
                <Link to="/" data-testid="brand-logo-link" className="group">
                    <Logo />
                </Link>

                <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `text-[14.5px] font-medium transition-colors ${
                                    isActive
                                        ? "text-[#0E2A47]"
                                        : "text-[#6F6B63] hover:text-[#0E2A47]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={BRAND.phoneHref}
                        className="flex items-center gap-2 text-[14px] font-bold text-[#C8A24A] hover:text-[#a98835] transition-colors"
                        data-testid="header-phone-link"
                    >
                        <Phone className="h-4 w-4" />
                        {BRAND.phone}
                    </a>
                    <Link
                        to="/referrals"
                        className="inline-flex items-center justify-center rounded-full bg-[#0E2A47] text-white px-5 py-2.5 text-[14px] font-medium hover:bg-[#08182B] transition-colors"
                        data-testid="header-referral-cta"
                    >
                        Make a Referral
                    </Link>
                </div>

                <button
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                    className="lg:hidden p-2 rounded-full text-[#1A2230] hover:bg-[#EFEBE0] transition-colors"
                    onClick={() => setOpen((o) => !o)}
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div
                    className="lg:hidden border-t border-[#E5DFD3] bg-[#F7F5F0]"
                    data-testid="mobile-nav"
                >
                    <div className="px-6 py-4 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`m-${l.testid}`}
                                className={({ isActive }) =>
                                    `py-2.5 px-3 rounded-lg text-[15px] font-medium transition-colors ${
                                        isActive
                                            ? "text-[#0E2A47] bg-[#EFEBE0]"
                                            : "text-[#1A2230] hover:bg-[#EFEBE0]"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/referrals"
                            onClick={() => setOpen(false)}
                            className="mt-3 inline-flex items-center justify-center rounded-full bg-[#0E2A47] text-white px-5 py-3 text-[14px] font-medium hover:bg-[#08182B]"
                            data-testid="m-header-referral-cta"
                        >
                            Make a Referral
                        </Link>
                        <a
                            href={BRAND.phoneHref}
                            className="mt-1 inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#C8A24A] text-[#C8A24A] px-5 py-3 text-[15px] font-bold"
                            data-testid="m-header-phone-link"
                        >
                            <Phone className="h-4 w-4" /> {BRAND.phone}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
